import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const PROVIDERS = [
  { id: "google", label: "Google (Gemini / Veo video)", hint: "Powers Generate Video — bills your Google account directly. Get it at aistudio.google.com" },
  { id: "openai", label: "OpenAI (GPT models)", hint: "Reserved — draft fallback wiring coming soon" },
  { id: "anthropic", label: "Anthropic (Claude models)", hint: "Reserved — draft fallback wiring coming soon" },
];

// Admin-only BYOK manager: keys are encrypted server-side, shown only masked.
export const ApiKeysCard = () => {
  const [keys, setKeys] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/settings/provider-keys");
      setKeys(data.keys || []);
    } catch {
      /* card stays empty */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (provider) => {
    const apiKey = (drafts[provider] || "").trim();
    if (!apiKey) return;
    setBusy(provider);
    try {
      await api.put("/settings/provider-keys", { provider, api_key: apiKey });
      toast.success(`${provider} key saved — encrypted, shown only masked`);
      setDrafts((d) => ({ ...d, [provider]: "" }));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (provider) => {
    setBusy(provider);
    try {
      await api.delete(`/settings/provider-keys/${provider}`);
      toast.success(`${provider} key removed`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const configured = (p) => keys.find((k) => k.provider === p);

  return (
    <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4" data-testid="api-keys-card">
      <div className="flex items-center gap-2 text-xs uppercase code-font text-cyan-700 dark:text-cyan-300 font-bold mb-1">
        <KeyRound className="w-3.5 h-3.5" /> API Keys (Your Own)
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3 flex items-start gap-1">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
        Keys are stored encrypted, never shown in full, and requests bill YOUR provider account — zero DSS AI credits.
      </p>
      <div className="space-y-3">
        {PROVIDERS.map((p) => {
          const k = configured(p.id);
          return (
            <div key={p.id} className="space-y-1" data-testid={`apikey-row-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{p.label}</span>
                {k?.configured && (
                  <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    <span className="font-mono">{k.masked}</span>
                    <button
                      type="button"
                      onClick={() => remove(p.id)}
                      disabled={busy === p.id}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      title="Remove key"
                      data-testid={`apikey-delete-${p.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <div className="flex gap-1.5">
                <input
                  type="password"
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 text-xs"
                  placeholder={k?.configured ? "Paste a new key to replace" : "Paste API key"}
                  value={drafts[p.id] || ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                  autoComplete="off"
                  data-testid={`apikey-input-${p.id}`}
                />
                <button
                  type="button"
                  onClick={() => save(p.id)}
                  disabled={busy === p.id || !(drafts[p.id] || "").trim()}
                  className="px-3 py-1.5 rounded-lg bg-cyan-700 text-white text-[11px] font-bold uppercase code-font hover:bg-cyan-800 transition-colors disabled:opacity-60"
                  data-testid={`apikey-save-${p.id}`}
                >
                  Save
                </button>
              </div>
              <div className="text-[10px] text-slate-400 dark:text-slate-500">{p.hint}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
